import { useState, useCallback, useRef } from 'react'
import * as Sentry from '@sentry/browser'
import checkIsOwnGoods from '@/modules/goods/utils/checkIsOwnGoods'
import { UPDATE_STATE_TYPE, FORM_TYPE } from '@/modules/goods/constants/goodsShelf'
import { ONLINE_ITEM, ONLINE_EDIT } from '@/modules/goods/constants/goodsSourceType'
import { useMemoizedFn } from 'ahooks'
import { getShopTypeToPlatformType } from '@/modules/globals/constants/platform/basics'
import { checkDistributionGoods, checkGoodsIsMe, showErpSkuStatus, asyncQueryCardDetailFields } from '@/services/goods'

/**
 * tips: 下面的类型散落在代码里面需要后续可以整理
 * 类型解释： https://alidocs.dingtalk.com/i/nodes/o14dA3GK8g5Lo3QOt6v7Le6aV9ekBD76?doc_type=wiki_doc# 「怎么区分拼款（超级拼款），组合上架，正常上架等
 * */
type SHELVES_TYPE = 'DISTRIBUTION' | 'SHELVES'
type COMBO_SHELVES_TYPE = 'ADD' | 'UPDATE'
type SHELVES_PATTERN_TYPE = 'DRP_COMBO' | 'MULTI_COMBO' | 'P_SPU_COMBO'
type GOODS_SOURCE_TYPE = 'GYL' | 'ERP_SUPPLIER' | 'ERP_DIRECT' | 'COMBINE' | 'ONLINE_ITEM' | 'ONLINE_EDIT'

/**
 *  铺货 上架页面 以下 itemSpuId, itemCoId, shopId必有
 * */
interface useGoodsShelfDataSource {
    /** 商品spuId, 部分场景下只初始化当前平台信息，没supId */
    itemSpuId?: string
    /** 商品coid，分销商品显示分销商coid */
    itemCoId?: string
    /** 商品店铺id, 编辑时候为系统配置店铺id，发布时候为选择的第一家店id */
    shopId?: string
    /** 多店铺铺货 店铺id */
    shopIds?: Array<string>
    /** 拼款上架 使用商品主id */
    mainSpuId?: string
    /** 发货模式 */
    shelvesType?: SHELVES_TYPE
    /** 拼款铺货类型 */
    comboShelvesType?: COMBO_SHELVES_TYPE
    /** 不同拼款铺货 */
    shelvesPattern?: SHELVES_PATTERN_TYPE
    /** 商品来源 */
    goodsSource?: GOODS_SOURCE_TYPE
    /** 店铺类型 比如(淘宝|头条放心购), 但是在 goodsSource === ONLINE_EDIT 中 shopType为platformKey */
    shopType: string
    /** 页面显示状态 */
    formType?: FORM_TYPE
}

interface $pageReturn {
    /** 页面form */
    $form: object | null
    /** 页面平台 */
    $platformKey: string | null
    /** 平台规则 */
    $platformRules?: any
    /** 页面资料更新 */
    $initPreEdit: (() => void) | null
    /** 页面load触发 */
    $setLoading?: React.Dispatch<React.SetStateAction<boolean>> | null
    /** 切换类目 */
    $changeCategory?: (() => void) | null
    /** 部分情况 */
    $setInitialValues?: (() => void) | null
    /** 保存成功以后的回调 */
    $onOk?: (() => void) | null
    /** 预测类目显示 */
    $setIsUseCategoryForecast?: React.Dispatch<React.SetStateAction<boolean>> | null
    /** 拉取基础资料 */
    $lazyGetBaseInfo?: (() => void) | null
}

interface platformData {
    [platformKey: string]: {
        /** 编辑|上架 参数 */
        dataSource: useGoodsShelfDataSource | {}
        /** 页面状态 (当前处于什么状态) */
        pageState: null | string
        /** 页面 form */
        $form: null
        /** 当前平台是否编辑过 */
        hasEdited: false
    }
}

interface useGoodsShelfReturn {
    /** 页面元素引用 */
    $page: $pageReturn
    /** 平台初始化 */
    init: (dataSource: useGoodsShelfDataSource) => void
    /** 平台卸载 */
    unload: () => void
    /** 更具type更新state状态 */
    updateState: ({ type, platformKey }: { type: UPDATE_STATE_TYPE; platformKey?: string }) => void
    onValuesChange: ({ platformKey }: { platformKey?: string }) => void
    /** 更新商品额外状态 */
    asyncExpiredPlatform: (dataSource: useGoodsShelfDataSource) => void
    /** 发布 */
    isCreate?: boolean
    /** 编辑 */
    isEdit?: boolean
    /** 完善 */
    isPerfect?: boolean
    /** 平台key (新平台类型), 以后的平台判断都应该走这个 */
    platformKey: string
    /** 平台状态 需要每个组件都要获取当前的平台以后获取对应的值 */
    platformData: platformData | {}
    /** 是否是组合铺货 */
    isComboShelves: boolean
    /** 是否店铺商品铺货 */
    isStoreGoodsQuickShelf: boolean
    /** 店铺商品编辑更新 */
    isStoreGoodsEditUpdate: boolean
    /** 是否自己的商品 (判断商品coid) */
    isOwnGoods?: boolean
    /** 异步拉取状态是否已经完成 (只有进入页面就会触发的场景才会用到) */
    asyncComplete?: boolean
    /** 是否代发商品 (通过接口判断,coid 等于自己 可能代发) */
    asyncIsDistributor?: boolean
    /** 是否自己的商品 (通过接口判断, 商品是否已经导入到自己的平台资料) */
    asyncIsOwnGoods?: boolean
    /** 判断了不同来源的商品是否是自已的 拼款，超极拼 ERP铺货 ERP铺供应商的货(具体问龙鸟) */
    asyncIsOwnErpSkuStatus?: boolean
    /** 页面停留时间 */
    staySeconds: number
}

const defaultState = {
    $page: {
        $form: null,
        $initPreEdit: null,
        $setLoading: null,
        $platformKey: null,
        $onOk: null,
        $setIsUseCategoryForecast: null,
    },
    platformData: {},
    isComboShelves: false,
    isStoreGoodsQuickShelf: false,
    isStoreGoodsEditUpdate: false,
    platformKey: '',
    asyncComplete: false,
    asyncIsDistributor: false,
    asyncIsOwnGoods: false,
    asyncIsOwnErpSkuStatus: false,
    isEdit: false,
    isPerfect: false,
    cardDetail: {} // 补全信息
}

// 解决异步调用接口在切花平台的时候重复执行的问题
class AsyncDataCache {
    public cache: {}
    public constructor() {
        this.cache = {}
    }

    public hasAsyncData(dataSource: useGoodsShelfDataSource) {
        const cacheKey = `${dataSource.itemCoId}_${dataSource.itemSpuId}`

        if (this.cache[cacheKey]) {
            return true
        }

        this.cache[cacheKey] = true
        return false
    }

    public unload() {
        this.cache = {}
    }
}

const asyncCache = new AsyncDataCache()

// 页面接口加载完成以后，延迟 3s, 判断是否用户手动编辑
const EDIT_DELAY_TIME = 3

export default (): useGoodsShelfReturn => {
    const [state, setState] = useState(defaultState)
    const [staySeconds, setStaySeconds] = useState(0)
    const interval = useRef<NodeJS.Timeout | null>(null)

    // 获取商品额外的信息，比如过期平台：perfectPlatformList
    const asyncExpiredPlatform = useCallback(async (dataSource: useGoodsShelfDataSource) => {
        const json = await asyncQueryCardDetailFields([
            {
                coId: dataSource.itemCoId,
                spuId: dataSource.itemSpuId,
            },
        ])

        if (json.success && Array.isArray(json.data)) {
            const cardItem = json.data.find((item2: { spuId: string }) => item2.spuId === dataSource.itemSpuId)

            setState(pre => {
                return {
                    ...pre,
                    cardDetail: cardItem,
                }
            })
        }
    }, [])

    const asyncInit = useCallback(async (dataSource: useGoodsShelfDataSource) => {
        if (asyncCache.hasAsyncData(dataSource)) {
            return
        }

        try {
            const obj = {
                asyncIsOwnGoods: false,
            }

            const [distributionJson, goodsIsMeJson, erpSkuStatusJson] = await Promise.all([
                checkDistributionGoods({
                    itemCoId: dataSource.itemCoId,
                    itemSpuId: dataSource.itemSpuId,
                }),
                checkGoodsIsMe({ itemSpuId: dataSource.itemSpuId }),
                showErpSkuStatus({
                    id: dataSource.itemSpuId,
                    itemCoId: dataSource.itemCoId,
                }),
            ])

            if (distributionJson?.success && distributionJson?.data) {
                Object.assign(obj, { asyncIsDistributor: true })
            }

            if (goodsIsMeJson?.success && goodsIsMeJson?.data) {
                Object.assign(obj, { asyncIsOwnGoods: true })
            }

            if (erpSkuStatusJson?.success && erpSkuStatusJson?.data) {
                Object.assign(obj, { asyncIsOwnErpSkuStatus: true })
            }

            Object.assign(obj, { asyncComplete: true })

            // 判断失效平台 自己的商品 编辑的时候获取, 暂时只有在供应测的商品管理才有这个逻辑
            if (window.MASTER_IS_SUPPLIER && dataSource.formType === FORM_TYPE.EDIT && obj.asyncIsOwnGoods) {
                asyncExpiredPlatform(dataSource)
            }

            setState(pre => {
                return {
                    ...pre,
                    ...obj,
                }
            })
        } catch (error) {
            console.error('catchError', error)
        }
    }, [])

    const init = useCallback(
        (dataSource: useGoodsShelfDataSource) => {
            const obj = { platformKey: dataSource.platformKey }

            if (dataSource?.formType === FORM_TYPE.CREATE) {
                Object.assign(obj, { isCreate: true })
            }

            if (dataSource?.formType === FORM_TYPE.EDIT) {
                Object.assign(obj, { isEdit: true })
            }

            if (dataSource?.formType === FORM_TYPE.PERFECT) {
                Object.assign(obj, { isPerfect: true })
            }

            // 转换 shopType 为 新平台类型
            if (dataSource?.shopType) {
                Object.assign(obj, { platformKey: getShopTypeToPlatformType(dataSource?.shopType) })
            }

            if (dataSource?.comboShelvesType) {
                Object.assign(obj, { isComboShelves: true })
            }

            if (dataSource?.goodsSource === ONLINE_ITEM) {
                Object.assign(obj, { isStoreGoodsQuickShelf: true })
            }

            if (dataSource?.goodsSource === ONLINE_EDIT) {
                Object.assign(obj, { isStoreGoodsEditUpdate: true })
            }

            if (dataSource?.itemCoId) {
                Object.assign(obj, {
                    isOwnGoods: checkIsOwnGoods({
                        itemCoId: dataSource.itemCoId,
                    }),
                })
            }

            if (obj.platformKey) {
                Object.assign(obj, {
                    platformData: {
                        [obj.platformKey]: { dataSource },
                    },
                })
            }

            setState(pre => {
                return {
                    ...defaultState,
                    ...pre,
                    ...obj,
                }
            })

            // 铺货模板管理不用初始化以下状态
            dataSource.itemSpuId && asyncInit(dataSource)

            // 记录页面等待时间
            interval.current && clearInterval(interval.current)
            setStaySeconds(0)
            interval.current = setInterval(() => {
                setStaySeconds(prev => prev + 1)
            }, 1000)

            // 记录 sentry
            Sentry.setTag('shelfData.itemSpuId', dataSource.itemSpuId)
            Sentry.setTag('shelfData.formType', dataSource.formType)
            Sentry.setTag('shelfData.itemCoId', dataSource.itemCoId)
            Sentry.setTag('shelfData.shopType', dataSource.shopType)
        },
        [asyncInit],
    )

    const unload = useCallback(() => {
        setState(defaultState)
        asyncCache.unload()

        // 清除 sentry
        Sentry.setTag('shelfData.itemSpuId', null)
        Sentry.setTag('shelfData.formType', null)
        Sentry.setTag('shelfData.itemCoId', null)
        Sentry.setTag('shelfData.shopType', null)
    }, [])

    const updateState = useMemoizedFn(({ type, platformKey, ...props }) => {
        // 如果当前的更新平台与触发平台不一样不操作
        if (!state.$page.$platformKey || state.$page.$platformKey !== platformKey) return

        // console.log('handleTabClick-正在修改数据-type', platformKey, type, staySeconds)

        const obj = {}

        switch (type) {
            case UPDATE_STATE_TYPE.PRE_INFO: {
                const platformData = state.platformData[platformKey]
                Object.assign(obj, {
                    platformData: {
                        [platformKey]: {
                            ...platformData,
                            beginTime: staySeconds,
                            pageState: UPDATE_STATE_TYPE.PRE_INFO,
                            ...props,
                        },
                    },
                })
                break
            }

            case UPDATE_STATE_TYPE.BASE_INFO: {
                const platformData = state.platformData[platformKey]
                Object.assign(obj, {
                    platformData: {
                        [platformKey]: {
                            ...platformData,
                            beginTime: staySeconds,
                            pageState: UPDATE_STATE_TYPE.BASE_INFO,
                            ...props,
                        },
                    },
                })
                break
            }

            case UPDATE_STATE_TYPE.HAS_EDITED: {
                const platformData = state.platformData[platformKey]

                // 计时初始化
                if (!props?.hasEdited) {
                    setStaySeconds(0)
                }

                Object.assign(obj, {
                    platformData: {
                        [platformKey]: {
                            ...platformData,
                            ...props,
                        },
                    },
                })
                break
            }

            default:
                break
        }

        setState(pre => ({
            ...pre,
            ...obj,
        }))
    })

    const onValuesChange = useMemoizedFn(({ platformKey }) => {
        try {
            if (!state.$page.$platformKey || state.$page.$platformKey !== platformKey) return

            const platformData = state.platformData[platformKey]
            const beginTime = platformData?.beginTime
            const hasEdited = platformData?.hasEdited

            console.log('savePlatformData-onValuesChange', {
                platformData,
                beginTime,
                hasEdited,
                staySeconds,
            })

            // 已经是修改状态
            if (hasEdited) {
                return
            }

            // 把前置应为接口触发表单变化的过滤掉
            if (staySeconds >= beginTime + EDIT_DELAY_TIME) {
                updateState({ type: UPDATE_STATE_TYPE.HAS_EDITED, platformKey, hasEdited: true })
            }
        } catch (error) {
            console.error('数据转换改变状态出错', error)
        }
    })

    return {
        init,
        unload,
        updateState,
        onValuesChange,
        asyncExpiredPlatform,
        staySeconds,
        ...state,
    }
}
